import { focusStyle } from '@/styles/variables';
import React, { InputHTMLAttributes } from 'react';
import styled from 'styled-components';
import { ReactComponent as ChevronDownIcon } from 'src/assets/icons/chevron-down.svg';

export type SelectInputOption = {
  value: string | number;
  text: string;
};

type Props = InputHTMLAttributes<HTMLSelectElement> & {
  label: string;
  options: SelectInputOption[];
  id?: string;
  hasError?: boolean;
  errorMessage?: string;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  className?: string;
};

const Select = ({
  value,
  label,
  options,
  id,
  errorMessage,
  hasError,
  onChange,
  className,
  ...rest
}: Props): JSX.Element => {
  return (
    <Wrapper className={className} hasError={hasError}>
      <CustomSelect
        value={value}
        id={id}
        name={id}
        onChange={onChange}
        hasError={hasError}
        {...rest}
      >
        {options.map((item) => {
          return (
            <option value={item.value} key={`${item.text}-${item.value}`}>
              {item.text}
            </option>
          );
        })}
      </CustomSelect>
      <Icon>
        <ChevronDownIcon width={20} height={20} />
      </Icon>
      <label htmlFor={id}>{label}</label>
      {hasError && errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </Wrapper>
  );
};

const Wrapper = styled.div<{ disabled?: boolean; hasError?: boolean }>`
  position: relative;
  width: 100%;
  min-width: fit-content;

  label {
    position: absolute;
    top: 0.3rem;
    left: 1rem;
    z-index: 10;
    font-size: 0.75rem;
    transition: all 0.1s ease;
    pointer-events: none;
    color: ${({ theme }) => theme.colors.black800};
    font-weight: bold;
  }

  ${({ hasError, theme }) =>
    hasError &&
    `
    label {
      color: ${theme.colors.warning} !important;
    }
  `}

  ${({ disabled, theme }) =>
    disabled &&
    `
       label {
          color: ${theme.colors.grey200};
        }
    `}
`;
const CustomSelect = styled.select<{
  hasError?: boolean;
  disabled?: boolean;
}>`
  appearance: none;
  padding: 1rem 1rem 0.3rem;
  width: 100%;
  height: 2.75rem;
  color: ${({ theme }) => theme.colors.black500};
  background-color: ${({ theme }) => theme.colors.white500};
  border: none;
  border-radius: 0.5rem;
  -webkit-appearance: none;
  -moz-appearance: none;

  ${({ hasError, theme }) =>
    hasError &&
    `
      margin-bottom: ${theme.space.sm};
      background-color: ${theme.colors.warningHover};
  `}

  ${({ disabled, theme }) =>
    disabled &&
    `
      background-color: ${theme.colors.grey500};
      cursor: not-allowed;
  `}
  ${({ required, theme }) =>
    required &&
    `
      + label:after {
        content: ' *';
        color: ${theme.colors.warning};
    }
  `}
  &:focus:not([disabled]) {
    ${focusStyle};
  }
`;

const Icon = styled.div`
  position: absolute;
  top: 32px;
  right: 8px;
  transform: translateY(-50%);
  pointer-events: none;
  svg {
    path {
      stroke: ${({ theme }) => theme.colors.black800};
      fill: ${({ theme }) => theme.colors.black800};
    }
  }
`;

const ErrorMessage = styled.span`
  display: block;
  width: 100%;
  margin-top: ${({ theme }) => theme.space.sm};
  color: ${({ theme }) => theme.colors.secondary500};
  font-size: 0.85rem;
`;

export default Select;
